import { Toaster } from "@/components/ui/sonner";
import { AgeVerificationModal } from "@/components/AgeVerificationModal";
import { MobileStorefrontHeader } from "@/components/MobileStorefrontHeader";
import { StorefrontFooter } from "@/components/StorefrontFooter";
import { CartProvider, useCart } from "@/contexts/CartContext";
import { Route, Switch, useLocation } from "wouter";
import ErrorBoundary from "./components/ErrorBoundary";
import { ThemeProvider } from "./contexts/ThemeContext";
import Home from "./pages/Home";
import { WholesaleApply, WholesaleDashboard, WholesaleLanding, WholesaleLogin, WholesalePasswordHelp, WholesaleVerifyEmail } from "./pages/Wholesale";
import { WholesaleAccount, WholesaleCart, WholesaleCatalog, WholesaleCheckout, WholesaleCheckoutConfirmation, WholesaleOrderHistory, WholesaleProductDetail, WholesaleQuickOrder, WholesaleSavedProducts, WholesaleShop } from "./pages/WholesaleCommerce";
import { lazy, Suspense } from "react";

const AdminOrdersDashboard = lazy(() => import("@/pages/AdminOrdersDashboard"));
const AllGummiesCollection = lazy(() => import("@/pages/AllGummiesCollection"));
const AllProductsCollection = lazy(() => import("@/pages/AllProductsCollection"));
const Checkout = lazy(() => import("@/pages/Checkout"));
const CheckoutConfirmation = lazy(() => import("@/pages/CheckoutConfirmation"));
const KryptCollection = lazy(() => import("@/pages/KryptCollection"));
const KryptDraftPreview = lazy(() => import("@/pages/KryptDraftPreview"));
const PowdersCollection = lazy(() => import("@/pages/FormatCollection").then((module) => ({ default: module.PowdersCollection })));
const CapsulesCollection = lazy(() => import("@/pages/FormatCollection").then((module) => ({ default: module.CapsulesCollection })));
const LegalInformationPage = lazy(() => import("@/pages/LegalInformationPage"));
const Learn = lazy(() => import("@/pages/Learn"));
const About = lazy(() => import("@/pages/About"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const ProductDetail = lazy(() => import("@/pages/ProductDetail"));
const WholesaleOperations = lazy(() => import("@/pages/WholesaleOperations"));
const WholesaleShopPreview = lazy(() => import("@/pages/WholesaleShopPreview"));
const CartDrawer = lazy(() => import("@/components/CartDrawer").then((module) => ({ default: module.CartDrawer })));

function DeferredCartDrawer() {
  const { isCartOpen } = useCart();

  if (!isCartOpen) {
    return null;
  }

  return (
    <Suspense fallback={null}>
      <CartDrawer />
    </Suspense>
  );
}

function AgeGateWithDevelopmentReviewException() {
  const [location] = useLocation();
  if (import.meta.env.DEV && (location.startsWith("/_review/krypt-products") || location.startsWith("/_review/wholesale-shop"))) {
    return null;
  }
  return <AgeVerificationModal />;
}

function PublicMobileNavigation() {
  const [location] = useLocation();
  const isProtectedFlow = location.startsWith("/wholesale") || location.startsWith("/checkout") || location.startsWith("/admin");

  return isProtectedFlow ? null : <MobileStorefrontHeader />;
}

function Router() {
  return (
    <Suspense
      fallback={
        <main className="flex min-h-screen items-center justify-center bg-[#06110f] px-6 text-center text-base text-white/72">
          Loading storefront…
        </main>
      }
    >
      <Switch>
        {import.meta.env.DEV ? <Route path="/_review/krypt-products" component={KryptDraftPreview} /> : null}
        {import.meta.env.DEV ? <Route path="/_review/wholesale-shop" component={WholesaleShopPreview} /> : null}
        <Route path="/" component={Home} />
        <Route path="/shop" component={AllProductsCollection} />
        <Route path="/gummies" component={AllGummiesCollection} />
        <Route path="/powders" component={PowdersCollection} />
        <Route path="/capsules" component={CapsulesCollection} />
        <Route path="/learn" component={Learn} />
        <Route path="/about" component={About} />
        <Route path="/wholesale" component={WholesaleLanding} />
        <Route path="/wholesale/apply" component={WholesaleApply} />
        <Route path="/wholesale/login" component={WholesaleLogin} />
        <Route path="/wholesale/verify" component={WholesaleVerifyEmail} />
        <Route path="/wholesale/password-help" component={WholesalePasswordHelp} />
        <Route path="/wholesale/dashboard" component={WholesaleDashboard} />
        <Route path="/wholesale/shop" component={WholesaleShop} />
        <Route path="/wholesale/catalog" component={WholesaleCatalog} />
        <Route path="/wholesale/products/:handle" component={WholesaleProductDetail} />
        <Route path="/wholesale/orders" component={WholesaleOrderHistory} />
        <Route path="/wholesale/saved-products" component={WholesaleSavedProducts} />
        <Route path="/wholesale/account" component={WholesaleAccount} />
        <Route path="/wholesale/quick-order" component={WholesaleQuickOrder} />
        <Route path="/wholesale/cart" component={WholesaleCart} />
        <Route path="/wholesale/checkout" component={WholesaleCheckout} />
        <Route path="/wholesale/checkout/confirmation" component={WholesaleCheckoutConfirmation} />
        <Route path="/krypt" component={KryptCollection} />
        <Route path="/products/:handle" component={ProductDetail} />
        <Route path="/checkout" component={Checkout} />
        <Route path="/checkout/confirmation" component={CheckoutConfirmation} />
        <Route path="/shipping-restrictions" component={LegalInformationPage} />
        <Route path="/fda-disclaimer" component={LegalInformationPage} />
        <Route path="/terms" component={LegalInformationPage} />
        <Route path="/privacy" component={LegalInformationPage} />
        <Route path="/refund-policy" component={LegalInformationPage} />
        <Route path="/shipping-policy" component={LegalInformationPage} />
        <Route path="/contact" component={LegalInformationPage} />
        <Route path="/admin/orders" component={AdminOrdersDashboard} />
        <Route path="/admin/wholesale" component={WholesaleOperations} />
        <Route path="/404" component={NotFound} />
        <Route component={NotFound} />
      </Switch>
    </Suspense>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider defaultTheme="dark">
        <CartProvider>
          <Toaster />
          <AgeGateWithDevelopmentReviewException />
          <DeferredCartDrawer />
          <PublicMobileNavigation />
          <Router />
          <StorefrontFooter />
        </CartProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
